// Manager for map computation Web Worker
// Handles heavy calculations off the main thread

class MapComputeWorkerManager {
  constructor() {
    this.worker = null;
    this.pendingRequests = new Map();
    this.requestId = 0;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      this.worker = new Worker('/mapComputeWorker.js');

      this.worker.onmessage = (e) => {
        const { id, type, result, error } = e.data;
        const request = this.pendingRequests.get(id);

        if (request) {
          this.pendingRequests.delete(id);
          if (type === 'viewportCalculated') {
            request.resolve(result);
          } else if (type === 'error') {
            request.reject(new Error(error || 'Worker error'));
          }
        }
      };

      this.worker.onerror = (error) => {
        console.error('[MapComputeWorker] Worker error:', error);
        // Reject all pending requests
        this.pendingRequests.forEach(request => {
          request.reject(new Error('Worker crashed'));
        });
        this.pendingRequests.clear();
      };

      this.isInitialized = true;
    } catch (error) {
      console.error('[MapComputeWorker] Failed to initialize:', error);
      throw error;
    }
  }

  async calculateViewport(bounds, zoom) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const id = this.requestId++;
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error('Viewport calculation timeout'));
      }, 5000);

      this.pendingRequests.set(id, {
        resolve: (result) => {
          clearTimeout(timeout);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeout);
          reject(error);
        }
      });

      // Convert Google Maps bounds to plain object
      const boundsData = {
        northEast: {
          lat: bounds.getNorthEast().lat(),
          lng: bounds.getNorthEast().lng()
        },
        southWest: {
          lat: bounds.getSouthWest().lat(),
          lng: bounds.getSouthWest().lng()
        }
      };

      this.worker.postMessage({
        id,
        type: 'calculateViewport',
        data: { bounds: boundsData, zoom }
      });
    });
  }

  terminate() {
    if (this.worker) {
      // Reject all pending requests
      this.pendingRequests.forEach(request => {
        request.reject(new Error('Worker terminated'));
      });
      this.pendingRequests.clear();

      this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
    }
  }
}

// Export singleton instance
export const mapComputeWorkerManager = new MapComputeWorkerManager();